import { useEffect, useRef, useState, useCallback, type ComponentType, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Globe,
  Pencil,
  CheckCircle,
  BadgeCheck,
  XCircle,
  RefreshCw,
  RotateCcw,
  Send,
  CalendarClock,
  Landmark,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Check,
  Ban,
  ShieldCheck,
  ClipboardList,
  Truck,
  UserPlus,
  Copy,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useDolfinAuth, useDolfinClient } from "@/components/DolfinProvider";
import { useIsDemo } from "@/demo/DemoContext";
import { useSupplier } from "@/hooks/useSupplier";
import { useBillStateChange } from "@/hooks/useBillStateChange";
import { BillDocumentViewer } from "@/components/BillDocumentViewer";
import { BillAttachmentsCard } from "@/components/BillAttachmentsCard";
import { BillMatchPanel } from "@/components/BillMatchPanel";
import { BillValidationCard } from "@/components/BillValidationCard";
import { AccountingSyncBadge, platformName } from "@/components/AccountingSync";
import { BillAccountingCard, BillLineCoding, billCodingVisible } from "@/components/BillAccounting";
import { accountLabel, type LedgerAccount, type PlatformTaxRate } from "@/components/AccountingSettingsSections";
import { isExpense, sortBy } from "@/lib/accounting-utils";
import type { ApAccountingSettings } from "@/hooks/useAccountingConnected";
import { SubmitBillReviewDialog } from "@/components/SubmitBillReviewDialog";
import { CreateSupplierDialog } from "@/components/CreateSupplierDialog";
import { PayByBankDialog, BillPaymentStatusCard } from "@/components/PayByBank";
import { BillPaymentsCard, type BillPaymentRecord } from "@/components/BillPaymentsCard";
import { usePermissions } from "@/lib/permissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import { apiErrorMessage } from "@/lib/api-error";
import { CopyButton } from "@/components/ui/copy-button";
import { InfoHint } from "@/components/InfoHint";
import { daysFromToday, formatCurrency, formatDate, formatDateTime, formatQuantity } from "@/lib/format";
import { stateLabel, stateVariant } from "@/lib/bill-utils";
import { ErrorState, LoadingState, Pager, Timeline } from "@/components/PageUi";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { DatePicker } from "@/components/ui/date-picker";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";

type BillResponse = components["schemas"]["BillResponse"];
type EInvoiceFault = components["schemas"]["EInvoiceFault"];
type SupplierResponse = components["schemas"]["SupplierResponse"];
type BillAuditLogResponse = components["schemas"]["BillAuditLogResponse"];
type BillLineItem = components["schemas"]["BillLineItemResponse"];
type ManualPaymentMethod = components["schemas"]["ManualPaymentMethod"];

const MANUAL_PAYMENT_METHODS: { value: ManualPaymentMethod; label: string }[] = [
  { value: "BankTransfer", label: "Bank transfer" },
  { value: "Cash", label: "Cash" },
  { value: "Cheque", label: "Cheque" },
  { value: "Card", label: "Card" },
  { value: "Other", label: "Other" },
];

const AUDIT_PAGE_SIZE = 10;

// From approval on, a bill can carry payments.
const PAYMENTS_VISIBLE_STATES = new Set([
  "Approved", "Scheduled", "PaymentInProgress", "PaymentFailed", "PartiallyPaid", "Paid",
]);

const SECTION_TITLE = "text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground";

// How the bill reached us, in words. The enum is the API's vocabulary.
const SOURCE_LABELS: Record<string, string> = {
  Upload: "Uploaded",
  Email: "Email",
  Webhook: "API",
  EInvoice: "E-invoice",
};


const USER_NAME_CACHE_KEY = "billAudit:userNames";

function readUserNameCache(): Record<string, string | null> {
  try {
    const raw = sessionStorage.getItem(USER_NAME_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeUserNameCache(cache: Record<string, string | null>) {
  try {
    sessionStorage.setItem(USER_NAME_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // session storage unavailable / full — drop silently
  }
}

function humanizeAction(action: string | undefined): string {
  if (!action) return "";
  return action.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/** One field in a dl grid. Renders nothing when there is no value, so empty fields don't leave holes. */
function DetailRow({ label, value }: { label: string; value: ReactNode | null | undefined }) {
  if (value == null || value === "") return null;
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium break-words">{value}</dd>
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number | string | null | undefined }) {
  if (confidence == null) return null;
  const value = typeof confidence === "string" ? parseFloat(confidence) : confidence;
  if (isNaN(value)) return null;

  const percent = Math.round(value * 100);
  let className = "text-xs ";
  if (value >= 0.7) className += "border-green-500/25 bg-green-500/15 text-green-600";
  else if (value >= 0.5) className += "border-amber-500/25 bg-amber-500/15 text-amber-600";
  else className += "border-red-500/25 bg-red-500/15 text-red-600";

  return <Badge className={className}>{percent}% confidence</Badge>;
}

function confidencePercent(confidence: number | string | null | undefined): number | null {
  if (confidence == null) return null;
  const value = typeof confidence === "string" ? parseFloat(confidence) : confidence;
  return isNaN(value) ? null : Math.round(value * 100);
}

/** A supplier suggestion at/above this confidence is treated as an exact ("Matched") hit. */
const SUPPLIER_MATCH_EXACT_THRESHOLD = 0.95;

/** "Matched" for a high-confidence (tax-id / email / exact-name) hit, "Likely" for a fuzzy one to confirm. */
function SupplierMatchChip({ confidence }: { confidence: number | string | null | undefined }) {
  const value = typeof confidence === "string" ? parseFloat(confidence) : confidence;
  if (value == null || isNaN(value)) return null;
  const pct = Math.round(value * 100);
  const exact = value >= SUPPLIER_MATCH_EXACT_THRESHOLD;
  const className = exact
    ? "text-xs font-normal border-green-500/25 bg-green-500/15 text-green-600"
    : "text-xs font-normal border-amber-500/25 bg-amber-500/15 text-amber-600";
  return (
    <Badge className={className}>
      {exact ? "Matched" : "Likely"} · {pct}%
    </Badge>
  );
}

/**
 * Confirmed (`categoryConfirmed`), AI (category equals the suggestion, auto-applied above 0.6), or a suggestion
 * awaiting accept. `suggested*` is kept forever, so confirmation is what flips the badge.
 */
function ClassificationBadge({
  item,
  categoryName,
}: {
  item: BillLineItem;
  categoryName: (key: string) => string;
}) {
  if (item.categoryConfirmed) {
    return (
      <Badge className="text-xs font-normal border-green-500/25 bg-green-500/15 text-green-600">
        <Check className="size-3" />
        Confirmed by you
      </Badge>
    );
  }
  const pct = confidencePercent(item.categoryConfidence);
  if (item.category && item.suggestedCategory && item.category === item.suggestedCategory) {
    return (
      <Badge className="text-xs font-normal border-violet-500/25 bg-violet-500/15 text-violet-600">
        <Sparkles className="size-3" />
        AI{pct != null ? ` · ${pct}%` : ""}
      </Badge>
    );
  }
  if (item.category == null && item.suggestedCategory) {
    return (
      <Badge className="text-xs font-normal border-amber-500/25 bg-amber-500/15 text-amber-600">
        Suggested: {categoryName(item.suggestedCategory)}
        {pct != null ? ` · ${pct}%` : ""}
      </Badge>
    );
  }
  return null;
}

export interface BillPageProps {
  billId: string;
  onBack?: () => void;
  onEditClick?: (billId: string) => void;
  onSupplierClick?: (supplierId: string) => void;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
  onMatchUiClick?: (billId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
  /** Navigate to another bill — used by the possible-duplicate banner to open the earlier bill. */
  onBillClick?: (billId: string) => void;
  /** SignalR host for live updates; without it the page still works, minus push. */
  hubBaseUrl?: string;
  /** Optional <Link> for the empty "no reviewers configured" state of the submit dialog. */
  apSettingsLink?: ReactNode;
  /** Optional pluggable Link component; only used if onSupplierClick is not provided. */
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  /** Given only when the client holds ap.accounting and a platform is connected. */
  accounting?: ApAccountingSettings | null;
  /** The host's Books card (S6), when the client holds `books`. */
  booksCard?: ReactNode;
}

/**
 * At the top, not in a tab: a fault must be decided on before approving, and the fields show only the value
 * that won. Not errors: the document said more than this system can hold.
 */
function BillFaultsCard({ faults }: { faults?: EInvoiceFault[] }) {
  if (!faults || faults.length === 0) return null;

  // A Stop (bank-details faults only) promotes the whole card: an e-invoiced bill can't be paid by bank, so
  // someone pays it by hand, and amber among notes isn't enough for "do not pay yet".
  const stopped = faults.some((f) => f.severity === "Stop");

  return (
    <Card
      className={
        stopped
          ? "border-red-500/50 bg-red-500/5"
          : "border-amber-500/40 bg-amber-500/5"
      }
    >
      <CardHeader>
        <CardTitle
          className={
            stopped
              ? "flex items-center gap-2 text-red-700 dark:text-red-400"
              : "flex items-center gap-2 text-amber-700 dark:text-amber-400"
          }
        >
          <AlertTriangle className="size-4" />
          {stopped
            ? "Check before paying"
            : faults.length === 1
              ? "Needs your attention"
              : `${faults.length} things need your attention`}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {faults.map((fault, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span
                className={
                  fault.severity === "Stop"
                    ? "mt-1.5 size-1.5 shrink-0 rounded-full bg-red-600"
                    : "mt-1.5 size-1.5 shrink-0 rounded-full bg-amber-600"
                }
              />
              <span>{fault.message}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function BillPage({
  billId,
  onBack,
  onEditClick,
  onSupplierClick,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
  onMatchUiClick,
  onCreditNoteClick,
  onBillClick,
  hubBaseUrl,
  apSettingsLink,
  LinkComponent,
  accounting,
  booksCard,
}: BillPageProps) {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const { can, demoReason } = usePermissions();
  const isDemo = useIsDemo();
  const canApprove = can("approveBills");
  const canMarkBillPaid = can("markBillPaid");
  const [bill, setBill] = useState<BillResponse | null>(null);
  // Read-only; the taxonomy is managed in the Portal.
  const [categories, setCategories] = useState<
    components["schemas"]["ResolvedCategoryResponse"][]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { supplier: matchedSupplier } = useSupplier(bill?.suggestedMatchedSupplierId);

  const [submitting, setSubmitting] = useState(false);
  const [confirmingSupplier, setConfirmingSupplier] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [retryingOcr, setRetryingOcr] = useState(false);
  const [confirmingPaid, setConfirmingPaid] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidPopoverOpen, setVoidPopoverOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [dismissingDuplicate, setDismissingDuplicate] = useState(false);

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [createSupplierOpen, setCreateSupplierOpen] = useState(false);
  const [approvePopoverOpen, setApprovePopoverOpen] = useState(false);
  const [rejectPopoverOpen, setRejectPopoverOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [confirmPaidPopoverOpen, setConfirmPaidPopoverOpen] = useState(false);
  const [confirmPaidMethod, setConfirmPaidMethod] = useState<ManualPaymentMethod>("BankTransfer");
  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<string | null>(null);
  const [payByBankOpen, setPayByBankOpen] = useState(false);
  const [payByBankResume, setPayByBankResume] = useState<{ amount: number; institutionId: string } | null>(null);
  // Client-level opt-in (managed in the Dolfin portal); hides the pay-by-bank action when off.
  const [payByBankEnabled, setPayByBankEnabled] = useState(false);

  const [creditApplications, setCreditApplications] = useState<
    components["schemas"]["SupplierCreditApplicationResponse"][]
  >([]);
  const [supplierCreditNotes, setSupplierCreditNotes] = useState<
    components["schemas"]["SupplierCreditNoteResponse"][]
  >([]);

  const [auditLogs, setAuditLogs] = useState<BillAuditLogResponse[]>([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(true);
  const [auditCursorStack, setAuditCursorStack] = useState<string[]>([]);
  const [auditNextCursor, setAuditNextCursor] = useState<string | null>(null);
  const [actorNames, setActorNames] = useState<Record<string, string | null>>(() => readUserNameCache());
  const inFlightActorIds = useRef<Set<string>>(new Set());

  // The chart the line pickers choose from, loaded only while a platform is connected.
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [platformTaxRates, setPlatformTaxRates] = useState<PlatformTaxRate[]>([]);
  // Bumped after a push so the credit applications reload their sync badges too.
  const [accountingRefreshes, setAccountingRefreshes] = useState(0);

  useEffect(() => {
    if (!accounting) return;
    let cancelled = false;
    Promise.all([api.GET("/v1/accounting/ledger-accounts", {}), api.GET("/v1/accounting/tax-rates", {})]).then(
      ([accountsResult, ratesResult]) => {
        if (cancelled) return;
        // Expense accounts first; purchase rates first (Xero types them INPUT; QuickBooks leaves type empty).
        setLedgerAccounts(sortBy(accountsResult.data ?? [], isExpense));
        setPlatformTaxRates(
          sortBy(
            (ratesResult.data ?? []).filter((r) => r.isActive),
            (r) => (r.type ?? "").toUpperCase().startsWith("INPUT"),
          ),
        );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, accounting]);

  // Reloads the bill in place, without the full-page loading state, for the accounting card's wait.
  const refreshBillQuietly = useCallback(async () => {
    const { data } = await api.GET("/v1/bills/{id}", { params: { path: { id: billId } } });
    if (data) setBill(data);
    setAccountingRefreshes((n) => n + 1);
  }, [api, billId]);

  const fetchBill = useCallback(async () => {
    if (!billId) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await api.GET("/v1/bills/{id}", {
        params: { path: { id: billId } },
      });
      if (apiError || !data) {
        console.error("Failed to load bill:", apiError);
        setError("Failed to load bill");
        return;
      }
      setBill(data);
    } catch (e) {
      console.error("Failed to load bill:", e);
      setError("Failed to load bill");
    } finally {
      setLoading(false);
    }
  }, [billId, api]);

  useEffect(() => {
    fetchBill();
  }, [fetchBill]);

  const [payments, setPayments] = useState<BillPaymentRecord[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const paymentsVisible = PAYMENTS_VISIBLE_STATES.has(bill?.state ?? "");
  // Books links a matched bank transaction here at #payments.
  useScrollToHash("payments", paymentsVisible && !paymentsLoading);
  useEffect(() => {
    if (!billId || !paymentsVisible) {
      setPayments([]);
      return;
    }
    let cancelled = false;
    setPaymentsLoading(true);
    api
      .GET("/v1/bills/{id}/payment-records", { params: { path: { id: billId } } })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError) console.error("Failed to load bill payments:", apiError);
        setPayments(data ?? []);
      })
      .finally(() => {
        if (!cancelled) setPaymentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, billId, paymentsVisible, bill?.amountPaid, bill?.updatedAt]);

  // Faults live on the e-invoice (one copy, as on /received-e-invoices); fetched only for a bill off the network.
  const [eInvoiceFaults, setEInvoiceFaults] = useState<EInvoiceFault[]>([]);
  const eInvoiceId = bill?.eInvoiceId;

  useEffect(() => {
    if (!eInvoiceId || !organisationId) {
      setEInvoiceFaults([]);
      return;
    }

    let cancelled = false;
    api
      .GET("/v1/received-e-invoices/{id}", {
        params: { path: { id: eInvoiceId } },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        // Deliberately quiet. The bill is fully usable without this; a toast here would put an error
        // in front of someone whose bill is fine, for a panel that is empty most of the time.
        if (error) {
          console.error("Failed to load the e-invoice behind this bill:", error);
          return;
        }
        setEInvoiceFaults(data?.faults ?? []);
      });

    return () => {
      cancelled = true;
    };
  }, [api, organisationId, eInvoiceId]);

  // Supplier credit netted against this bill. Re-fetches when AmountCredited changes.
  // The credit-note list is only for nice labels on the applications.
  useEffect(() => {
    if (!billId) return;
    let cancelled = false;
    (async () => {
      const [appsRes, scnRes] = await Promise.all([
        api.GET("/v1/bills/{billId}/credit-applications", { params: { path: { billId } } }),
        api.GET("/v1/supplier-credit-notes", { params: { query: { Limit: 100 } } }),
      ]);
      if (!cancelled) {
        setCreditApplications(appsRes.data ?? []);
        setSupplierCreditNotes(scnRes.data?.data ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, billId, bill?.amountCredited, accountingRefreshes]);

  useEffect(() => {
    let cancelled = false;
    api.GET("/v1/payables/categories").then(({ data }) => {
      if (cancelled) return;
      if (!data) {
        // Non-fatal (names fall back to raw keys), but say so.
        toast.error("Failed to load spend categories");
      }
      setCategories(data ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    // Reads resolve the owning client from org context; only writes are client-admin.
    api.GET("/v1/settings/ap", {}).then(({ data }) => {
      if (cancelled) return;
      // Non-fatal: when this fails the pay-by-bank button just stays hidden.
      setPayByBankEnabled(data?.payByBankEnabled ?? false);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const categoryName = useCallback(
    (key: string) => categories.find((c) => c.key === key)?.displayName ?? key,
    [categories],
  );

  const fetchAuditLogs = useCallback(
    async (cursor?: string) => {
      if (!billId) return;
      setAuditLogsLoading(true);
      try {
        const query: Record<string, string | number> = { Limit: AUDIT_PAGE_SIZE };
        if (cursor) query.Cursor = cursor;
        const { data, error: apiError } = await api.GET("/v1/bills/{billId}/audit-logs", {
          params: { path: { billId }, query },
        });
        if (apiError || !data) {
          console.error("Failed to load audit logs:", apiError);
          return;
        }
        setAuditLogs(data.data ?? []);
        setAuditNextCursor(data.meta?.cursors?.next ?? null);
      } catch (e) {
        console.error("Failed to load audit logs:", e);
      } finally {
        setAuditLogsLoading(false);
      }
    },
    [billId, api],
  );

  useEffect(() => {
    setAuditCursorStack([]);
    setAuditNextCursor(null);
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  useEffect(() => {
    const missing: string[] = [];
    for (const log of auditLogs) {
      if (
        log.actorType === "User" &&
        log.actorId &&
        !(log.actorId in actorNames) &&
        !inFlightActorIds.current.has(log.actorId)
      ) {
        missing.push(log.actorId);
      }
    }
    if (missing.length === 0) return;
    for (const id of missing) inFlightActorIds.current.add(id);

    let cancelled = false;
    Promise.all(
      missing.map(async (id) => {
        const { data } = await api.GET("/v1/users/{id}", { params: { path: { id } } });
        return [id, data?.name?.trim() || data?.email || null] as const;
      }),
    ).then((results) => {
      for (const [id] of results) inFlightActorIds.current.delete(id);
      if (cancelled) return;
      setActorNames((prev) => {
        const next = { ...prev };
        for (const [id, name] of results) next[id] = name;
        writeUserNameCache(next);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditLogs]);

  // Resolve names for the bill's policy-resolved approvers (and a manual voider)
  // into the same actorNames cache used by the audit log.
  useEffect(() => {
    const ids = [
      ...(bill?.resolvedApproverUserIds ?? []),
      ...(bill?.voidedBy ? [bill.voidedBy] : []),
      ...payments.flatMap((p) => (p.createdBy ? [p.createdBy] : [])),
    ];
    const missing = ids.filter(
      (id) => !(id in actorNames) && !inFlightActorIds.current.has(id),
    );
    if (missing.length === 0) return;
    for (const id of missing) inFlightActorIds.current.add(id);

    let cancelled = false;
    Promise.all(
      missing.map(async (id) => {
        const { data } = await api.GET("/v1/users/{id}", { params: { path: { id } } });
        return [id, data?.name?.trim() || data?.email || null] as const;
      }),
    ).then((results) => {
      for (const [id] of results) inFlightActorIds.current.delete(id);
      if (cancelled) return;
      setActorNames((prev) => {
        const next = { ...prev };
        for (const [id, name] of results) next[id] = name;
        writeUserNameCache(next);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill?.resolvedApproverUserIds, bill?.voidedBy, payments]);

  function handleAuditNextPage() {
    if (!auditNextCursor) return;
    setAuditCursorStack((prev) => [...prev, auditNextCursor]);
    fetchAuditLogs(auditNextCursor);
  }

  function handleAuditPrevPage() {
    const newStack = [...auditCursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setAuditCursorStack(newStack);
    fetchAuditLogs(prevCursor);
  }

  const auditCurrentPage = auditCursorStack.length + 1;

  // Extracting, PendingReview and NeedsApproval get `billClassified` without a state change; PaymentInProgress
  // gets provider status moves, flipping to Paid or PaymentFailed without user action.
  useBillStateChange(
    billId,
    bill?.state === "Extracting" ||
      bill?.state === "OcrProcessing" || // obsolete alias, drain
      bill?.state === "PendingReview" ||
      bill?.state === "NeedsApproval" ||
      bill?.state === "PaymentInProgress",
    fetchBill,
    { hubBaseUrl },
  );

  async function handleSubmitReview() {
    if (!billId || submitting) return;
    setSubmitting(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/submit-review", {
        params: { path: { id: billId } },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to submit for review"));
        return;
      }
      setBill(data);
      setSubmitDialogOpen(false);
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Bill submitted for approval");
    } catch {
      toast.error("Failed to submit for review");
    } finally {
      setSubmitting(false);
    }
  }

  // Accept the fuzzy supplier suggestion by explicitly linking it as the bill's
  // supplier (PATCH supplierId). Clears the "confirm?" prompt and the submit gate.
  async function handleConfirmSupplier() {
    if (!billId || confirmingSupplier || !bill?.suggestedMatchedSupplierId) return;
    setConfirmingSupplier(true);
    try {
      const { data, error: apiError } = await api.PATCH("/v1/bills/{id}", {
        params: { path: { id: billId } },
        body: { supplierId: bill.suggestedMatchedSupplierId },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to confirm supplier"));
        return;
      }
      setBill(data);
      toast.success("Supplier confirmed");
    } catch {
      toast.error("Failed to confirm supplier");
    } finally {
      setConfirmingSupplier(false);
    }
  }

  // The bill had no matched or suggested supplier — the reviewer created one from the extracted
  // details. Link it onto the bill (PATCH supplierId) so matching can run, mirroring handleConfirmSupplier.
  async function handleSupplierCreated(supplier: SupplierResponse) {
    if (!billId || !supplier.id) return;
    try {
      const { data, error: apiError } = await api.PATCH("/v1/bills/{id}", {
        params: { path: { id: billId } },
        body: { supplierId: supplier.id },
      });
      if (apiError || !data) {
        toast.error(
          apiErrorMessage(apiError, "Supplier created, but linking it to the bill failed"),
        );
        return;
      }
      setBill(data);
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Supplier linked to bill");
    } catch {
      toast.error("Supplier created, but linking it to the bill failed");
    }
  }

  async function handleApprove() {
    if (!billId || approving) return;
    setApproving(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/approve", {
        params: { path: { id: billId } },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to approve bill"));
        return;
      }
      setBill(data);
      setApprovePopoverOpen(false);
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Bill approved");
    } catch {
      toast.error("Failed to approve bill");
    } finally {
      setApproving(false);
    }
  }

  // "Not a duplicate" — the reviewer's verdict that clears the block on approving this bill. The link to
  // the other bill is kept by the backend, so the banner stays (in its resolved form) as a record.
  async function handleDismissDuplicate() {
    if (!billId || dismissingDuplicate) return;
    setDismissingDuplicate(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/dismiss-duplicate", {
        params: { path: { id: billId } },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to confirm this bill is not a duplicate"));
        return;
      }
      setBill(data);
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Marked as not a duplicate");
    } catch {
      toast.error("Failed to confirm this bill is not a duplicate");
    } finally {
      setDismissingDuplicate(false);
    }
  }

  async function handleReject() {
    if (!billId || rejecting) return;
    setRejecting(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/reject", {
        params: { path: { id: billId } },
        body: { reason: rejectReason.trim() || null },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to reject bill"));
        return;
      }
      setBill(data);
      setRejectPopoverOpen(false);
      setRejectReason("");
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Bill rejected");
    } catch {
      toast.error("Failed to reject bill");
    } finally {
      setRejecting(false);
    }
  }

  async function handleReopen() {
    if (!billId || reopening) return;
    setReopening(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/reopen", {
        params: { path: { id: billId } },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to reopen bill"));
        return;
      }
      setBill(data);
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Bill reopened for editing");
    } catch {
      toast.error("Failed to reopen bill");
    } finally {
      setReopening(false);
    }
  }

  async function handleVoid() {
    if (!billId || voiding) return;
    setVoiding(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/void", {
        params: { path: { id: billId } },
        body: { reason: voidReason.trim() || null },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to void bill"));
        return;
      }
      setBill(data);
      setVoidPopoverOpen(false);
      setVoidReason("");
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Bill voided");
    } catch {
      toast.error("Failed to void bill");
    } finally {
      setVoiding(false);
    }
  }

  async function handleConfirmPaid() {
    if (!billId || confirmingPaid) return;
    setConfirmingPaid(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/confirm-paid", {
        params: { path: { id: billId } },
        body: { paymentMethod: confirmPaidMethod },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to confirm bill as paid"));
        return;
      }
      setBill(data);
      setConfirmPaidPopoverOpen(false);
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Bill confirmed as paid");
    } catch {
      toast.error("Failed to confirm bill as paid");
    } finally {
      setConfirmingPaid(false);
    }
  }

  // Default the scheduled payment to the day before the due date so the bill
  // clears in time; fall back to today when the bill has no due date.
  function defaultScheduleDate(): string {
    const base = bill?.dueDate ? new Date(bill.dueDate) : new Date();
    if (bill?.dueDate) base.setDate(base.getDate() - 1);
    const yyyy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, "0");
    const dd = String(base.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  function openSchedulePopover(open: boolean) {
    if (open) setScheduleDate((prev) => prev ?? defaultScheduleDate());
    setSchedulePopoverOpen(open);
  }

  async function handleSchedulePayment() {
    if (!billId || scheduling || !scheduleDate) return;
    setScheduling(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/schedule-payment", {
        params: { path: { id: billId } },
        body: { paymentDate: scheduleDate },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to schedule bill payment"));
        return;
      }
      setBill(data);
      setSchedulePopoverOpen(false);
      fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
      toast.success("Bill scheduled for payment");
    } catch {
      toast.error("Failed to schedule bill payment");
    } finally {
      setScheduling(false);
    }
  }

  // Allowed only in review states (the backend 400s otherwise); refreshed suggestions arrive via `billClassified`.
  async function handleReclassify() {
    if (!billId || reclassifying) return;
    setReclassifying(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/bills/{id}/reclassify", {
        params: { path: { id: billId } },
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to re-classify bill"));
        return;
      }
      setBill(data);
      toast.success("Re-classifying - updated suggestions will appear shortly");
    } catch {
      toast.error("Failed to re-classify bill");
    } finally {
      setReclassifying(false);
    }
  }

  async function handleRetryOcr() {
    if (!billId || retryingOcr) return;
    setRetryingOcr(true);
    try {
      const { error: apiError } = await api.POST("/v1/bills/{id}/retry-extraction", {
        params: { path: { id: billId } },
      });
      if (apiError) {
        toast.error(apiErrorMessage(apiError, "Failed to retry extraction"));
        return;
      }
      setBill((prev) => (prev ? { ...prev, state: "Extracting" } : prev));
      toast.success("Extraction retried");
    } catch {
      toast.error("Failed to retry extraction");
    } finally {
      setRetryingOcr(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <LoadingState label="Loading bill..." />
      </div>
    );
  }

  if (error || !bill) {
    return (
      <div className="p-6">
        <ErrorState message={error || "That bill could not be found."} onRetry={fetchBill} />
      </div>
    );
  }

  const isPendingReview = bill.state === "PendingReview";

  // An e-invoice is the sender's regulated document: every PATCH is refused (409 Bill.NotEditableEInvoice),
  // so edit affordances come off and the reason is shown.
  const isEInvoice = bill.source === "EInvoice";
  const isNeedsApproval = bill.state === "NeedsApproval";
  const isRejected = bill.state === "Rejected";
  const isApproved = bill.state === "Approved";
  const isScheduled = bill.state === "Scheduled";
  const isExtractionFailed = bill.state === "ExtractionFailed" || bill.state === "OcrFailed";
  const isProcessing =
    bill.state === "Created" || bill.state === "Extracting" || bill.state === "OcrProcessing";
  const isPaymentInProgress = bill.state === "PaymentInProgress";
  const isPaymentFailed = bill.state === "PaymentFailed";
  const isPaid = bill.state === "Paid";
  const isPartiallyPaid = bill.state === "PartiallyPaid";
  const isVoided = bill.state === "Voided";

  // Blocks only approval, until confirmed not a duplicate; the link is kept after, so the banner stays as a record.
  const flaggedAsDuplicate = !!bill.duplicateOfBillId;
  const duplicateBlocksApproval = flaggedAsDuplicate && !bill.duplicateDismissedAt;

  // A suggestion not yet accepted. Exact hits (tax-id/email/name) are safe to auto-accept; fuzzy ones need confirming.
  const supplierUnconfirmed = !!bill.suggestedMatchedSupplierId && !bill.supplierId;
  const supplierMatchIsExact =
    Number(bill.suggestedMatchedSupplierConfidence ?? 0) >= SUPPLIER_MATCH_EXACT_THRESHOLD;
  // Whether the reviewer should be told the supplier is a guess. Independent of whether anything can
  // be done about it: a low-confidence match on an e-invoice is exactly as wrong as on any other bill.
  const supplierNeedsAcknowledging =
    isPendingReview && supplierUnconfirmed && !supplierMatchIsExact;
  // Confirmation is a PATCH too, so refused on an e-invoice; the review dialog still asks for acknowledgement.
  const needsSupplierConfirm = supplierNeedsAcknowledging && !isEInvoice;

  // What an uncoded line posts to: the supplier's default account, else the organisation's.
  const accountName = (id?: string | null) => {
    const account = id ? ledgerAccounts.find((a) => a.id === id) : null;
    return account ? accountLabel(account) : "an archived account";
  };
  const defaultAccountLabel = bill.supplier?.defaultLedgerAccountId
    ? `Supplier default: ${accountName(bill.supplier.defaultLedgerAccountId)}`
    : accounting?.defaultExpenseLedgerAccountId
      ? `Default: ${accountName(accounting.defaultExpenseLedgerAccountId)}`
      : "Default account (not set yet)";
  const showLineCoding = Boolean(accounting) && billCodingVisible(bill);

  const scnNumber = new Map(
    supplierCreditNotes.filter((s) => s.id).map((s) => [s.id!, s.documentNumber] as const),
  );
  // Void is allowed from these pre-payment, non-terminal states (backend 400s otherwise).
  const canVoidState = isExtractionFailed || isPendingReview || isNeedsApproval || isRejected;
  const canPayByBank =
    payByBankEnabled &&
    (isApproved || isScheduled || isPaymentFailed || isPaymentInProgress || isPartiallyPaid) &&
    ["GBP", "EUR"].includes(bill.currency?.toUpperCase() ?? "");
  // An abandoned attempt returns the bill to Approved, Scheduled or PartiallyPaid, where the card offers Resume. It
  // renders nothing for a bill with no bank payments.
  const showPaymentCard =
    isPaymentInProgress ||
    isPaymentFailed ||
    isApproved ||
    isScheduled ||
    isPartiallyPaid ||
    (isPaid && bill.paymentMethod === "OpenBanking");

  function renderSupplierLink(supplierId: string, name: string, className = "text-sm font-semibold") {
    if (onSupplierClick) {
      return (
        <button
          type="button"
          onClick={() => onSupplierClick(supplierId)}
          className={cn(className, "cursor-pointer text-left hover:underline")}
        >
          {name}
        </button>
      );
    }
    if (LinkComponent) {
      return (
        <LinkComponent href={`/suppliers/${supplierId}`} className={cn(className, "hover:underline")}>
          {name}
        </LinkComponent>
      );
    }
    return <p className={className}>{name}</p>;
  }

  const partyName = bill.supplier?.name || bill.supplierName || "Unknown supplier";
  // Only while the bill is still owed: once paid, voided or rejected, how late it "is" stops mattering.
  const dueOpen = isPendingReview || isNeedsApproval || isApproved || isScheduled || isPaymentFailed;
  const dueIn = bill.dueDate && dueOpen ? daysFromToday(bill.dueDate) : null;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between gap-2 px-6 py-3 border-b shrink-0">
        <button
          type="button"
          onClick={() => (onBack ? onBack() : window.history.back())}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Back to bills
        </button>
        <div className="flex gap-2">
          {isPendingReview && !isEInvoice && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => onEditClick?.(billId)}>
                  <Pencil className="size-4" />
                  Edit
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit extracted fields</TooltipContent>
            </Tooltip>
          )}
          {isEInvoice && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="gap-1.5 py-1.5">
                  <Globe className="size-3.5" />
                  E-invoice
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                Received over an e-invoicing network. Its contents are the sender&apos;s document and
                cannot be edited here.
              </TooltipContent>
            </Tooltip>
          )}
          {(isPendingReview || isNeedsApproval) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={reclassifying}
                  onClick={handleReclassify}
                >
                  {reclassifying ? (
                    <Spinner className="size-4" />
                  ) : (
                    <Sparkles className="size-4" />
                  )}
                  Re-classify
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Re-run AI classification on lines you haven&apos;t confirmed
              </TooltipContent>
            </Tooltip>
          )}
          {onMatchUiClick && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onMatchUiClick(billId)}
                >
                  <span className="flex items-center -space-x-1">
                    <ClipboardList className="size-4" />
                    <Truck className="size-4" />
                  </span>
                  Match
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Match against a Purchase Order or Delivery Note, or both
              </TooltipContent>
            </Tooltip>
          )}
          {isProcessing && (
            <Button variant="outline" size="sm" onClick={fetchBill}>
              <RefreshCw className="size-4" />
              Refresh
            </Button>
          )}
        </div>
      </div>

      <ResizablePanelGroup orientation="horizontal" className="flex-1">
        <ResizablePanel defaultSize={50} minSize={25}>
          <div className="h-full bg-muted/30">{billId && <BillDocumentViewer billId={billId} />}</div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={50} minSize={25}>
          <div className="h-full overflow-auto px-6 py-6">
            <div className="max-w-2xl mx-auto space-y-4">
              <BillFaultsCard faults={eInvoiceFaults} />
              <Card className="bg-card/60 backdrop-blur-sm">
                <CardContent className="space-y-5 pt-6">
                  <div className="@container">
                  <div className="flex flex-col gap-3 @lg:flex-row @lg:items-start @lg:justify-between @lg:gap-4">
                    <div className="min-w-0">
                      <div>
                        <p className={SECTION_TITLE}>
                          Bill{bill.invoiceNumber ? ` · ${bill.invoiceNumber}` : ""}
                        </p>
                        <div className="mt-0.5 break-words">
                          {bill.supplier && bill.supplierId ? (
                            renderSupplierLink(bill.supplierId, bill.supplier.name, "text-xl font-bold tracking-tight")
                          ) : (
                            <p className="text-xl font-bold tracking-tight">{partyName}</p>
                          )}
                        </div>
                        {bill.dueDate ? (
                          <p className="text-sm text-muted-foreground">
                            Due {formatDate(bill.dueDate, "long")}
                            {dueIn != null ? (
                              <span
                                className={cn(
                                  "tabular-nums",
                                  dueIn < 0 && "font-medium text-red-600 dark:text-red-400",
                                )}
                              >
                                {" · "}
                                {dueIn < 0
                                  ? `${-dueIn} ${dueIn === -1 ? "day" : "days"} overdue`
                                  : dueIn === 0
                                    ? "today"
                                    : `in ${dueIn} ${dueIn === 1 ? "day" : "days"}`}
                              </span>
                            ) : null}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 @lg:text-right">
                      <p className="text-2xl font-bold tabular-nums tracking-tight">
                        {formatCurrency(bill.totalAmount, bill.currency ?? "GBP")}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 @lg:justify-end">
                        <Badge variant={stateVariant(bill.state)}>{stateLabel(bill.state)}</Badge>
                        <ConfidenceBadge confidence={bill.confidence} />
                      </div>
                    </div>
                  </div>
                  </div>

                  {bill.suggestedMatchedSupplierId && matchedSupplier && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">
                          {supplierUnconfirmed ? "Suggested Supplier" : "Matched Supplier"}
                        </p>
                        <SupplierMatchChip
                          confidence={bill.suggestedMatchedSupplierConfidence}
                        />
                      </div>
                      {needsSupplierConfirm ? (
                        <div className="mt-1.5 rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5">
                          <p className="text-sm">
                            Did you mean{" "}
                            <span className="font-semibold">{matchedSupplier.name}</span>
                            {confidencePercent(bill.suggestedMatchedSupplierConfidence) != null
                              ? ` (${confidencePercent(
                                  bill.suggestedMatchedSupplierConfidence,
                                )}%)`
                              : ""}
                            ?
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={handleConfirmSupplier}
                              disabled={confirmingSupplier}
                            >
                              {confirmingSupplier ? (
                                <Spinner className="size-3" />
                              ) : (
                                <Check className="size-3" />
                              )}
                              Confirm
                            </Button>
                            {onEditClick && billId && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => onEditClick(billId)}
                              >
                                Choose another
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        renderSupplierLink(
                          bill.suggestedMatchedSupplierId,
                          matchedSupplier.name,
                        )
                      )}
                    </div>
                  )}
                  {isPendingReview &&
                    !bill.supplierId &&
                    !bill.suggestedMatchedSupplierId &&
                    // Creating ends in a PATCH of supplierId, which an e-invoice refuses, so show the reason instead.
                    !isEInvoice && (
                      <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-2.5">
                        <p className="text-sm font-medium">This looks like a new supplier.</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Create one now to enable 3-way match.
                        </p>
                        <div className="mt-2">
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setCreateSupplierOpen(true)}
                          >
                            <UserPlus className="size-3" />
                            Create supplier
                          </Button>
                        </div>
                      </div>
                    )}
                  {isPendingReview && !bill.supplierId && isEInvoice && (
                    <div className="mt-3 rounded-lg border border-muted-foreground/20 bg-muted/40 p-2.5">
                      <p className="text-sm font-medium">Supplier not linked.</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        An e-invoice cannot be edited after it arrives, and linking a supplier is an
                        edit. The bill can still be reviewed, approved and paid manually.
                      </p>
                    </div>
                  )}

                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t pt-4">
                    <DetailRow label="Invoice number" value={bill.invoiceNumber} />
                    <DetailRow
                      label="Invoice date"
                      value={bill.invoiceDate ? formatDate(bill.invoiceDate, "long") : null}
                    />
                    {bill.state === "Paid" && bill.paidAt ? (
                      <DetailRow label="Paid on" value={formatDate(bill.paidAt, "long")} />
                    ) : null}
                    <DetailRow label="Payment terms" value={bill.paymentTerms} />
                    {bill.billPoNumbers && bill.billPoNumbers.length > 0 ? (
                      <DetailRow
                        label={bill.billPoNumbers.length === 1 ? "PO number on the bill" : "PO numbers on the bill"}
                        value={
                          <>
                            {/* One line per reference, not a joined run: a bill covering two orders is
                                showing two separate things. */}
                            {bill.billPoNumbers.map((poNumber) => (
                              <span key={poNumber} className="block">
                                {poNumber}
                              </span>
                            ))}
                            {/* The search keys the bill is found by, shown so it is obvious why a search for
                                differently-punctuated text found it. Only when they differ from the raw values. */}
                            {bill.normalisedBillPoNumbers &&
                              bill.normalisedBillPoNumbers.join("\u0000") !== bill.billPoNumbers.join("\u0000") && (
                                <span className="block font-mono text-xs font-normal text-muted-foreground">
                                  {bill.normalisedBillPoNumbers.join(", ")}
                                </span>
                              )}
                          </>
                        }
                      />
                    ) : null}
                    {/* Provenance for everything else on the page: an e-invoiced bill's figures came from
                        the sender's accounting system, an uploaded one's were read off a scan. */}
                    <DetailRow
                      label="Source"
                      value={bill.source ? (SOURCE_LABELS[bill.source] ?? bill.source) : null}
                    />
                    {/* Truncated, never wrapped: a machine-generated file name is one unbreakable run. The
                        full name lives in the tooltip and the copy button. */}
                    {bill.fileName ? (
                      <div className="min-w-0">
                        <dt className="text-xs text-muted-foreground">File</dt>
                        <dd className="mt-0.5 flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="min-w-0 truncate text-sm font-medium">{bill.fileName}</span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs break-all">{bill.fileName}</TooltipContent>
                          </Tooltip>
                          <CopyButton value={bill.fileName} className="shrink-0" />
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                </CardContent>
              </Card>

              {isEInvoice && billId && <BillAttachmentsCard billId={billId} />}

              {/* flaggedAsDuplicate is in this gate because the flag is raised at extraction, so a flagged
                  bill sits in PendingReview with no policy, no approvers and no void — none of the other
                  conditions — and the warning would never render. */}
              {(bill.appliedApprovalPolicyName ||
                (isNeedsApproval && bill.resolvedApproverUserIds) ||
                isVoided ||
                flaggedAsDuplicate) && (
                <Card>
                  <CardHeader>
                    <CardTitle className={SECTION_TITLE}>
                      Approval
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {bill.appliedApprovalPolicyName && (
                      <div className="flex flex-col gap-1.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className="w-fit gap-1 border-accent-blue/30 bg-accent-blue/5 text-accent-blue"
                            >
                              <ShieldCheck className="size-3" />
                              Approval rule: {bill.appliedApprovalPolicyName}
                            </Badge>
                          </TooltipTrigger>
                          {bill.approvalReasoning && (
                            <TooltipContent className="max-w-xs">
                              {bill.approvalReasoning}
                            </TooltipContent>
                          )}
                        </Tooltip>
                        {bill.approvalReasoning && (
                          <p className="text-xs text-muted-foreground">{bill.approvalReasoning}</p>
                        )}
                      </div>
                    )}

                    {isNeedsApproval && (
                      <div>
                        <p className="text-xs text-muted-foreground">Waiting on</p>
                        <p className="text-sm font-medium">
                          {bill.resolvedApproverUserIds && bill.resolvedApproverUserIds.length > 0
                            ? bill.resolvedApproverUserIds
                                .map((id) => actorNames[id] ?? "a teammate")
                                .join(", ")
                            : "Awaiting an admin"}
                        </p>
                      </div>
                    )}

                    {flaggedAsDuplicate && (
                      <div
                        className={
                          duplicateBlocksApproval
                            ? "rounded-lg border border-amber-500/25 bg-amber-500/5 p-3"
                            : "rounded-lg border bg-muted/30 p-3"
                        }
                      >
                        <div className="flex items-start gap-2">
                          <Copy
                            className={
                              duplicateBlocksApproval
                                ? "mt-0.5 size-4 shrink-0 text-amber-600"
                                : "mt-0.5 size-4 shrink-0 text-muted-foreground"
                            }
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className={
                                duplicateBlocksApproval
                                  ? "text-sm font-medium text-amber-700 dark:text-amber-400"
                                  : "text-sm font-medium"
                              }
                            >
                              {duplicateBlocksApproval
                                ? "Possible duplicate bill"
                                : "Confirmed not a duplicate"}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {duplicateBlocksApproval
                                ? "This looks like the same invoice as an earlier bill: same supplier and invoice number. It can't be approved until you confirm it's a separate invoice."
                                : "This bill was flagged as a possible duplicate and cleared for approval."}
                              {bill.duplicateMatchBasis === "SupplierName" && duplicateBlocksApproval
                                ? " The supplier isn't on file yet, so this was matched on the supplier name printed on the document. Check it carefully."
                                : ""}
                            </p>

                            {bill.duplicateOfBillId && onBillClick && (
                              <button
                                type="button"
                                className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                onClick={() => onBillClick(bill.duplicateOfBillId!)}
                              >
                                View the earlier bill
                                <ArrowRight className="size-3" />
                              </button>
                            )}

                            {duplicateBlocksApproval && (
                              <PermissionGuard
                                allowed={canApprove}
                                reason="Only owners and admins can resolve a possible duplicate"
                                className="mt-2.5 inline-block"
                              >
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={dismissingDuplicate}
                                  onClick={handleDismissDuplicate}
                                >
                                  {dismissingDuplicate && <Spinner className="size-4" />}
                                  Not a duplicate
                                </Button>
                              </PermissionGuard>
                            )}

                            {!duplicateBlocksApproval && bill.duplicateDismissedAt && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {formatDateTime(bill.duplicateDismissedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {isVoided && (
                      <div className="rounded-lg border border-red-500/25 bg-red-500/5 p-3">
                        <p className="text-sm font-medium text-red-600">
                          {bill.voidedAt && !bill.voidedBy
                            ? `Auto-voided by rule${
                                bill.appliedApprovalPolicyName
                                  ? `: ${bill.appliedApprovalPolicyName}`
                                  : ""
                              }`
                            : "Voided - re-upload to start again"}
                        </p>
                        {bill.voidedBy && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            By {actorNames[bill.voidedBy] ?? "an admin"}
                            {bill.voidedAt ? ` · ${formatDateTime(bill.voidedAt)}` : ""}
                          </p>
                        )}
                        {bill.voidReason && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            Reason: {bill.voidReason}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {showPaymentCard && billId && (
                <BillPaymentStatusCard
                  billId={billId}
                  refreshKey={bill}
                  onResume={
                    canPayByBank && canMarkBillPaid && can("triggerPayment")
                      ? (payment) => {
                          setPayByBankResume({ amount: Number(payment.amount ?? 0), institutionId: payment.institutionId });
                          setPayByBankOpen(true);
                        }
                      : undefined
                  }
                />
              )}

              {(bill.supplierEmail ||
                bill.supplierPhone ||
                bill.supplierAddress ||
                bill.deliveryAddress ||
                bill.supplierTaxId ||
                bill.supplierWebsite ||
                bill.supplierRegistrationNumber) && (
                <Card>
                  <CardHeader>
                    <CardTitle className={SECTION_TITLE}>
                      Supplier details on the bill
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                      <DetailRow label="Email" value={bill.supplierEmail} />
                      <DetailRow label="Phone" value={bill.supplierPhone} />
                      <DetailRow label="Address" value={bill.supplierAddress} />
                      <DetailRow label="Delivery address (ship-to)" value={bill.deliveryAddress} />
                      <DetailRow label="Tax ID" value={bill.supplierTaxId} />
                      <DetailRow label="Website" value={bill.supplierWebsite} />
                      <DetailRow label="Registration number" value={bill.supplierRegistrationNumber} />
                    </dl>
                  </CardContent>
                </Card>
              )}

              {bill.lineItems && bill.lineItems.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className={SECTION_TITLE}>
                      Line items
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {showLineCoding && bill.accountingSync?.status === "Synced" ? (
                      <p className="mb-3 text-xs text-muted-foreground">
                        This bill is in {platformName(bill.accountingSync.platform)}, so where its lines post is
                        changed there now.
                      </p>
                    ) : null}
                    <ul className="divide-y rounded-xl border bg-card/60">
                      {bill.lineItems.map((item) => (
                        <li key={item.id} className="flex items-start justify-between gap-4 px-3 py-3">
                          <div className="@container min-w-0 flex-1">
                            <p className="text-sm font-semibold">{item.description}</p>
                            <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                              {item.sku ? <span className="font-mono">{item.sku} · </span> : null}
                              {formatQuantity(item.quantity, item.unit, item.unitOfMeasure)} ×{" "}
                              {formatCurrency(item.unitPrice, bill.currency ?? "GBP")}
                              {Number(item.taxRate) > 0 ? ` · ${item.taxRate}% tax` : ""}
                            </p>
                            {(item.category || item.spendClass || item.suggestedCategory) && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                {item.category && (
                                  <Badge variant="secondary" className="text-xs font-normal">
                                    {categoryName(item.category)}
                                  </Badge>
                                )}
                                {item.spendClass && (
                                  <Badge variant="outline" className="text-xs font-normal">
                                    {item.spendClass}
                                  </Badge>
                                )}
                                <ClassificationBadge item={item} categoryName={categoryName} />
                              </div>
                            )}
                            {item.tags && item.tags.length > 0 && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <span className="text-xs text-muted-foreground">Tags</span>
                                {item.tags.map((tag) => (
                                  <Badge key={tag} variant="outline" className="text-xs font-normal">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {showLineCoding ? (
                              <BillLineCoding
                                bill={bill}
                                line={item}
                                accounts={ledgerAccounts}
                                taxRates={platformTaxRates}
                                defaultAccountLabel={defaultAccountLabel}
                                onSaved={setBill}
                              />
                            ) : null}
                          </div>
                          <p className="shrink-0 text-sm font-semibold tabular-nums">
                            {formatCurrency(item.subTotal, bill.currency ?? "GBP")}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {billId && (
                <BillMatchPanel
                  billId={billId}
                  currency={bill.currency ?? "GBP"}
                  editable={isPendingReview}
                  onPurchaseOrderClick={onPurchaseOrderClick}
                  onDeliveryNoteClick={onDeliveryNoteClick}
                  onOpenMatchUi={onMatchUiClick ? () => onMatchUiClick(billId) : undefined}
                />
              )}

              <Card>
                <CardHeader>
                  <CardTitle className={SECTION_TITLE}>
                    Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span className="tabular-nums">{formatCurrency(bill.subTotal, bill.currency ?? "GBP")}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Tax</span>
                    <span className="tabular-nums">{formatCurrency(bill.taxAmount, bill.currency ?? "GBP")}</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-bold">
                    <span>Total</span>
                    <span className="tabular-nums">{formatCurrency(bill.totalAmount, bill.currency ?? "GBP")}</span>
                  </div>
                  {Number(bill.amountCredited ?? 0) > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
                        <span>Supplier credit</span>
                        <span>−{formatCurrency(bill.amountCredited, bill.currency ?? "GBP")}</span>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between font-bold">
                        <span>Outstanding</span>
                        <span className="tabular-nums">{formatCurrency(bill.outstanding, bill.currency ?? "GBP")}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {paymentsVisible && (
                <div id="payments" className="scroll-mt-4">
                <BillPaymentsCard
                  bill={bill}
                  payments={payments}
                  loading={paymentsLoading}
                  canRecord={canMarkBillPaid}
                  accountingEnabled={Boolean(accounting)}
                  titleClassName={SECTION_TITLE}
                  nameOf={(id) => actorNames[id]}
                  onChanged={() => {
                    refreshBillQuietly();
                    fetchAuditLogs(auditCursorStack[auditCursorStack.length - 1]);
                  }}
                />
                </div>
              )}

              {creditApplications.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className={SECTION_TITLE}>
                      Supplier credit applied
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {creditApplications.map((a) => {
                      const reversed = a.status === "Reversed";
                      const label =
                        (a.supplierCreditNoteId && scnNumber.get(a.supplierCreditNoteId)) || "Credit note";
                      return (
                        <div key={a.id} className="flex items-center justify-between text-sm">
                          {onCreditNoteClick && a.supplierCreditNoteId ? (
                            <button
                              type="button"
                              className="font-mono text-accent-blue hover:underline"
                              onClick={() => onCreditNoteClick(a.supplierCreditNoteId!)}
                            >
                              {label}
                            </button>
                          ) : (
                            <span className="font-mono">{label}</span>
                          )}
                          <span className="flex items-center gap-3">
                            {accounting ? <AccountingSyncBadge sync={a.accountingSync} waitsFor="bill" /> : null}
                            <span
                              className={
                                reversed
                                  ? "tabular-nums text-muted-foreground line-through"
                                  : "tabular-nums text-emerald-600 dark:text-emerald-400"
                              }
                            >
                              −{formatCurrency(a.amount, a.currency)}
                              {reversed ? " (reversed)" : ""}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {accounting ? (
                <BillAccountingCard bill={bill} titleClassName={SECTION_TITLE} onRefresh={refreshBillQuietly} />
              ) : null}

              {booksCard}

              {bill.paymentLinkUrl && (
                <Card>
                  <CardHeader>
                    <CardTitle className={cn(SECTION_TITLE, "flex items-center gap-1.5")}>
                      Payment link
                      <InfoHint label="the payment link">
                        Share this link with whoever pays the bill. It is also emailed to your admins when the bill
                        is approved.
                      </InfoHint>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex items-center gap-1">
                    <a
                      href={bill.paymentLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
                    >
                      {bill.paymentLinkUrl}
                    </a>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Copy payment link"
                      onClick={() => {
                        navigator.clipboard.writeText(bill.paymentLinkUrl!).then(
                          () => toast.success("Payment link copied"),
                          () => toast.error("Could not copy the payment link"),
                        );
                      }}
                    >
                      <Copy className="size-4" />
                    </Button>
                    <Button variant="ghost" size="icon-sm" asChild>
                      <a
                        href={bill.paymentLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open payment link in a new tab"
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              )}

              {(bill.paymentRef ||
                bill.bankDetails?.accountNumber ||
                bill.bankDetails?.sortCode ||
                bill.bankDetails?.iban) && (
                <Card>
                  <CardHeader>
                    <CardTitle className={SECTION_TITLE}>
                      Payment details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 [&_dd]:font-mono [&_dd]:tabular-nums">
                      <DetailRow label="Payment reference" value={bill.paymentRef} />
                      <DetailRow label="Account number" value={bill.bankDetails?.accountNumber} />
                      <DetailRow label="Sort code" value={bill.bankDetails?.sortCode} />
                      <DetailRow label="IBAN" value={bill.bankDetails?.iban} />
                    </dl>
                  </CardContent>
                </Card>
              )}

              <BillValidationCard billId={billId} />

              <Card>
                <CardHeader>
                  <CardTitle className={SECTION_TITLE}>
                    History
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {auditLogsLoading && auditLogs.length === 0 ? (
                    <div className="flex justify-center py-6">
                      <Spinner className="size-5 text-muted-foreground" />
                    </div>
                  ) : auditLogs.length === 0 ? (
                    <p className="py-2 text-sm text-muted-foreground">Nothing has happened to this bill yet.</p>
                  ) : (
                    <Timeline
                      entries={auditLogs.map((log) => ({
                        id: log.id ?? `${log.action}-${log.createdAt}`,
                        title: humanizeAction(log.action),
                        at: log.createdAt,
                        detail: (
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                            {log.fromState && (
                              <Badge variant={stateVariant(log.fromState)} className="text-[10px]">
                                {stateLabel(log.fromState)}
                              </Badge>
                            )}
                            {log.fromState && log.toState && (
                              <ArrowRight className="size-3 text-muted-foreground" aria-label="to" />
                            )}
                            {log.toState && (
                              <Badge variant={stateVariant(log.toState)} className="text-[10px]">
                                {stateLabel(log.toState)}
                              </Badge>
                            )}
                            <span>
                              {log.actorType === "System"
                                ? "Automatically"
                                : `by ${(log.actorId && actorNames[log.actorId]) || "a team member"}`}
                            </span>
                          </div>
                        ),
                      }))}
                    />
                  )}
                  <Pager
                    page={auditCurrentPage}
                    hasPrevious={auditCurrentPage > 1}
                    hasNext={!!auditNextCursor}
                    onPrevious={handleAuditPrevPage}
                    onNext={handleAuditNextPage}
                    disabled={auditLogsLoading}
                  />
                </CardContent>
              </Card>

              <div className="flex flex-col gap-2 pt-2">
                {isPendingReview && (
                  <PermissionGuard
                    allowed={canApprove}
                    reason="Only owners and admins can submit bills for review"
                    className="w-full"
                  >
                    <Button
                      className="w-full text-base font-semibold"
                      size="lg"
                      disabled={submitting}
                      onClick={() => setSubmitDialogOpen(true)}
                    >
                      {submitting ? <Spinner className="size-5" /> : <Send className="size-5" />}
                      Submit Bill for Review
                    </Button>
                  </PermissionGuard>
                )}

                {isNeedsApproval && !canApprove && (
                  <PermissionGuard
                    allowed={false}
                    reason="Only owners and admins can approve bills"
                    className="w-full"
                  >
                    <Button
                      className="w-full text-base font-semibold"
                      size="lg"
                    >
                      <CheckCircle className="size-5" />
                      Approve
                    </Button>
                  </PermissionGuard>
                )}

                {/* The backend refuses approval until it's confirmed a separate invoice; surface that rather than a 409. */}
                {isNeedsApproval && canApprove && duplicateBlocksApproval && (
                  <PermissionGuard
                    allowed={false}
                    reason="Confirm this isn't a duplicate before approving it"
                    className="w-full"
                  >
                    <Button
                      className="w-full text-base font-semibold"
                      size="lg"
                    >
                      <CheckCircle className="size-5" />
                      Approve
                    </Button>
                  </PermissionGuard>
                )}

                {isNeedsApproval && canApprove && !duplicateBlocksApproval && (
                  <Popover open={approvePopoverOpen} onOpenChange={setApprovePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full text-base font-semibold"
                        size="lg"
                      >
                        <CheckCircle className="size-5" />
                        Approve
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <p className="text-sm font-medium mb-3">Are you sure you want to approve this bill?</p>
                      <div className="flex gap-2">
                        <Button size="sm" disabled={approving} onClick={handleApprove}>
                          {approving && <Spinner className="size-4" />}
                          Confirm
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setApprovePopoverOpen(false)}>
                          Cancel
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {isNeedsApproval && !canApprove && (
                  <PermissionGuard
                    allowed={false}
                    reason="Only owners and admins can reject bills"
                    className="w-full"
                  >
                    <Button className="w-full text-base font-semibold" size="lg" variant="destructive">
                      <XCircle className="size-5" />
                      Reject
                    </Button>
                  </PermissionGuard>
                )}

                {isNeedsApproval && canApprove && (
                  <Popover open={rejectPopoverOpen} onOpenChange={setRejectPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button className="w-full text-base font-semibold" size="lg" variant="destructive">
                        <XCircle className="size-5" />
                        Reject
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <p className="text-sm font-medium mb-3">Reject this bill?</p>
                      <Textarea
                        placeholder="Reason for rejection (optional)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        className="mb-3"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button variant="destructive" size="sm" disabled={rejecting} onClick={handleReject}>
                          {rejecting && <Spinner className="size-4" />}
                          Reject
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setRejectPopoverOpen(false)}>
                          Cancel
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {isRejected && (
                  <PermissionGuard
                    allowed={canApprove}
                    reason="Only owners and admins can reopen bills"
                    className="w-full"
                  >
                    <Button
                      className="w-full text-base font-semibold"
                      size="lg"
                      variant="outline"
                      disabled={reopening}
                      onClick={handleReopen}
                    >
                      {reopening ? <Spinner className="size-5" /> : <RotateCcw className="size-5" />}
                      Reopen for Editing
                    </Button>
                  </PermissionGuard>
                )}

                {canPayByBank && canMarkBillPaid && (
                  <PermissionGuard
                    allowed={can("triggerPayment")}
                    reason="Only owners and admins can pay bills"
                    demoReason={demoReason("triggerPayment")}
                    className="w-full"
                  >
                    <Button
                      className="w-full text-base font-semibold"
                      size="lg"
                      onClick={() => {
                        setPayByBankResume(null);
                        setPayByBankOpen(true);
                      }}
                    >
                      <Landmark className="size-5" />
                      {isPaymentInProgress
                        ? "Restart Pay by Bank"
                        : isPaymentFailed
                          ? "Retry Pay by Bank"
                          : "Pay by Bank"}
                    </Button>
                  </PermissionGuard>
                )}

                {isApproved && !canMarkBillPaid && (
                  <PermissionGuard
                    allowed={false}
                    reason="Only owners and admins can confirm bills as paid"
                    className="w-full"
                  >
                    <Button
                      className="w-full text-base font-semibold"
                      size="lg"
                    >
                      <BadgeCheck className="size-5" />
                      Confirm as Paid
                    </Button>
                  </PermissionGuard>
                )}

                {isApproved && canMarkBillPaid && (
                  <Popover open={confirmPaidPopoverOpen} onOpenChange={setConfirmPaidPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full text-base font-semibold"
                        size="lg"
                      >
                        <BadgeCheck className="size-5" />
                        Confirm as Paid
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <p className="text-sm font-medium mb-3">
                        Confirm that this bill has been paid?
                      </p>
                      <Label htmlFor="confirm-paid-method" className="mb-1.5 block text-xs">
                        How was it paid?
                      </Label>
                      <Select
                        value={confirmPaidMethod}
                        onValueChange={(v) => setConfirmPaidMethod(v as ManualPaymentMethod)}
                      >
                        <SelectTrigger id="confirm-paid-method" className="mb-3 w-full" size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MANUAL_PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button size="sm" disabled={confirmingPaid} onClick={handleConfirmPaid}>
                          {confirmingPaid && <Spinner className="size-4" />}
                          Confirm
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmPaidPopoverOpen(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {isApproved && canMarkBillPaid && (
                  <Popover open={schedulePopoverOpen} onOpenChange={openSchedulePopover}>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full text-base font-semibold"
                        size="lg"
                        variant="outline"
                      >
                        <CalendarClock className="size-5" />
                        Schedule Bill for Payment
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <p className="text-sm font-medium mb-3">
                        Schedule this bill to be paid on a future date.
                      </p>
                      <Label htmlFor="schedule-payment-date" className="mb-1.5 block text-xs">
                        Payment date
                      </Label>
                      <DatePicker
                        value={scheduleDate}
                        onChange={setScheduleDate}
                        placeholder="Pick a payment date"
                        className="mb-3"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={scheduling || !scheduleDate}
                          onClick={handleSchedulePayment}
                        >
                          {scheduling && <Spinner className="size-4" />}
                          Schedule
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSchedulePopoverOpen(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {(isScheduled || isPartiallyPaid) && !canMarkBillPaid && (
                  <PermissionGuard
                    allowed={false}
                    reason="Only owners and admins can confirm bills as paid"
                    className="w-full"
                  >
                    <Button
                      className="w-full text-base font-semibold"
                      size="lg"
                    >
                      <BadgeCheck className="size-5" />
                      Confirm as Paid
                    </Button>
                  </PermissionGuard>
                )}

                {(isScheduled || isPaymentFailed || isPartiallyPaid) && canMarkBillPaid && (
                  <Popover open={confirmPaidPopoverOpen} onOpenChange={setConfirmPaidPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full text-base font-semibold"
                        size="lg"
                        variant={isPaymentFailed ? "outline" : "default"}
                      >
                        <BadgeCheck className="size-5" />
                        {isPaymentFailed
                          ? "Paid Another Way - Confirm as Paid"
                          : isPartiallyPaid
                            ? "Confirm Rest as Paid"
                            : "Confirm as Paid"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <p className="text-sm font-medium mb-3">
                        {isPaymentFailed
                          ? "Confirm this bill was paid outside Dolfin?"
                          : isPartiallyPaid
                            ? `Confirm the remaining ${formatCurrency(bill.outstanding, bill.currency ?? "GBP")} has been paid?`
                            : "Confirm that this scheduled bill has been paid?"}
                      </p>
                      <Label htmlFor="confirm-paid-method-scheduled" className="mb-1.5 block text-xs">
                        How was it paid?
                      </Label>
                      <Select
                        value={confirmPaidMethod}
                        onValueChange={(v) => setConfirmPaidMethod(v as ManualPaymentMethod)}
                      >
                        <SelectTrigger
                          id="confirm-paid-method-scheduled"
                          className="mb-3 w-full"
                          size="sm"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MANUAL_PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button size="sm" disabled={confirmingPaid} onClick={handleConfirmPaid}>
                          {confirmingPaid && <Spinner className="size-4" />}
                          Confirm
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmPaidPopoverOpen(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}

                {isExtractionFailed && !isDemo && (
                  <Button
                    className="w-full text-base font-semibold"
                    size="lg"
                    variant="outline"
                    disabled={retryingOcr}
                    onClick={handleRetryOcr}
                  >
                    {retryingOcr ? <Spinner className="size-5" /> : <RefreshCw className="size-5" />}
                    Retry extraction
                  </Button>
                )}

                {canVoidState && !canApprove && (
                  <PermissionGuard
                    allowed={false}
                    reason="Only owners and admins can void bills"
                    className="w-full"
                  >
                    <Button className="w-full text-sm font-medium" size="lg" variant="ghost">
                      <Ban className="size-4" />
                      Void bill
                    </Button>
                  </PermissionGuard>
                )}

                {canVoidState && canApprove && (
                  <Popover open={voidPopoverOpen} onOpenChange={setVoidPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        className="w-full text-sm font-medium text-destructive hover:text-destructive hover:bg-destructive/10"
                        size="lg"
                        variant="ghost"
                      >
                        <Ban className="size-4" />
                        Void bill
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <p className="text-sm font-medium mb-1">Void this bill?</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        This is permanent. To process it again you&apos;ll need to upload it afresh.
                      </p>
                      <Textarea
                        placeholder="Reason (optional)"
                        value={voidReason}
                        onChange={(e) => setVoidReason(e.target.value)}
                        className="mb-3"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button variant="destructive" size="sm" disabled={voiding} onClick={handleVoid}>
                          {voiding && <Spinner className="size-4" />}
                          Void bill
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setVoidPopoverOpen(false)}>
                          Cancel
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <SubmitBillReviewDialog
        open={submitDialogOpen}
        onOpenChange={setSubmitDialogOpen}
        submitting={submitting}
        onConfirm={handleSubmitReview}
        settingsLink={apSettingsLink}
        unconfirmedSupplierName={supplierNeedsAcknowledging ? matchedSupplier?.name : undefined}
        unconfirmedSupplierConfidence={
          supplierNeedsAcknowledging
            ? (confidencePercent(bill.suggestedMatchedSupplierConfidence) ?? undefined)
            : undefined
        }
      />

      {billId && (
        <PayByBankDialog
          open={payByBankOpen}
          onOpenChange={setPayByBankOpen}
          billId={billId}
          outstanding={bill.outstanding ?? bill.totalAmount}
          currency={bill.currency ?? "GBP"}
          supplierName={bill.supplier?.name ?? bill.supplierName}
          resume={payByBankResume}
        />
      )}

      <CreateSupplierDialog
        open={createSupplierOpen}
        onOpenChange={setCreateSupplierOpen}
        onCreated={handleSupplierCreated}
        description="Create a supplier from this bill's extracted details and link it to the bill."
        initialValues={{
          name: bill.supplierName,
          email: bill.supplierEmail,
          phone: bill.supplierPhone,
          website: bill.supplierWebsite,
          taxNumber: bill.supplierTaxId,
          registrationNumber: bill.supplierRegistrationNumber,
          addressLine1: bill.supplierAddress,
        }}
      />
    </div>
  );
}
